/* eslint-disable no-plusplus */
import { useState, useRef, useEffect } from "react";
import { useAuth } from "features/auth";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { Loading, Overlay } from "@components";
import { UserRoleEnum } from "features/users/types";

import { useFetchTranslationItems } from "features/workspaces/hooks/useFetchTranslationItems";
import { useFetchLanguage } from "features/languages/hooks/useFetchLanguage";
import { useFetchValidationRules } from "features/languages/hooks/useFetchValidationRules";
import { useFetchValidationTemplate } from "features/languages/hooks/useFetchValidationTemplate";

import { isRoleHigherOrEqual } from "features/users/utils";

import { TranslationItem } from "features/editor/components/Item/TranslationItem";
import { Panels } from "features/editor/components/Panels";
import { SearchBar } from "features/editor/components/toolbar/SearchBar";
import { usePanelHeight } from "features/editor/hooks/usePanelHeight";
import { EditorProvider, useEditorContext } from "features/editor/context/editor";
import { EDITOR_PAGE_SIZE_KEY } from "features/editor";

import type { IProjectId, IProjectLanguageId } from "features/projects/types";
import type { IWorkspaceId, IFileId, IEditorFetchParams } from "features/workspaces/types";
import type { ValidationRules } from "features/languages/types";

const EditorContent = () => {
  const loaderRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLInputElement>(null);
  const {
    list,
    resetListItems,
    activeItemIndex,
    goTo,
    isTaskProcessing,
    setActiveItemIndex,
    setActivePanel,
    setListItemErrors,
    setListItemApprove,
    setListItem,
    onTaskProcessAbort,
    approvedCount,
    setApprovedCount,
    syncApprovedCount,
  } = useEditorContext();

  const { fileId } = useParams() as unknown as {
    workspaceId: IWorkspaceId;
    projectId: IProjectId;
    languageId: IProjectLanguageId;
    fileId: IFileId;
  };

  const [fetchParams, setFetchParams] = useState<IEditorFetchParams>({
    fileId,
    page: 0,
    pageSize: localStorage.getItem(EDITOR_PAGE_SIZE_KEY) ?? window.appConfig.editor.pageSize,
    orderBy: "id",
    descendingOrder: false,
    search: "",
    searchIdInFile: "",
    searchOriginal: "",
    searchTranslation: "",
    searchGroup: "",
    showInvalidOnly: false,
    searchExact: false,
    searchGroupFullMatch: true,
  });

  // Fetch translation items
  const {
    totalCount,
    isLoading: isListLoading,
    isoCodeTranslation,
    validationTemplateId,
  } = useFetchTranslationItems(fetchParams, { setApprovedCount });

  // Fetch Language configuration
  const [languageTranslation, isLanguageFetching] = useFetchLanguage(isoCodeTranslation);
  const [validationTemplate, isValidationTemplateFetching] = useFetchValidationTemplate(validationTemplateId); // TODO: Fix this
  const [validationRules, isValidationRulesFetching] = useFetchValidationRules();

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isLoading = isListLoading || isLanguageFetching || isValidationRulesFetching || isValidationTemplateFetching;

  const fetchNextPage = () => {
    setFetchParams({ ...fetchParams, page: fetchParams.page + 1 });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const target = entries[0];
      if (!isLoading && target.isIntersecting && totalCount > Number(list.items.length) && list.items.length) {
        fetchNextPage();
      }
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [fetchParams, isLoading, list.items.length]);

  const onSearch = (
    query: string,
    searchField: "searchIdInFile" | "searchOriginal" | "searchTranslation" | "searchGroup",
    fullMatchField?: "searchGroupFullMatch",
    fullMatchFlag?: boolean,
  ) => {
    resetListItems();
    setFetchParams(prev => ({
      ...prev,
      page: 0,
      [searchField]: query,
      ...(fullMatchField ? { [fullMatchField]: fullMatchFlag } : {}),
    }));
  };

  // Refresh the data
  const handleRefresh = () => {
    resetListItems();
    setFetchParams((prev: any) => ({ ...prev, page: 0 }));
  };

  // Panels
  const { onPanelResizeStart, onPanelResize, onPanelResizeEnd } = usePanelHeight(panelRef);

  // Can approve
  const { userRole } = useAuth();
  const canApprove = isRoleHigherOrEqual(userRole, UserRoleEnum.Translator);

  const canLoadMore = totalCount > Number(list.items.length);

  return (
    <div
      role="none"
      onMouseMove={onPanelResize}
      onMouseUp={onPanelResizeEnd}
      style={{
        position: "absolute",
        top: 50,
        right: 24,
        bottom: 0,
        left: 24,

        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      <Overlay isVisible={isTaskProcessing} onProcessAbort={onTaskProcessAbort} />
      <SearchBar
        onSearch={onSearch}
        setFetchParams={setFetchParams}
        resetListItems={resetListItems}
        approvedCount={approvedCount}
        totalCount={totalCount}
        handleRefresh={handleRefresh}
        goTo={goTo}
        fetchParams={fetchParams}
        isLoading={isLoading}
      />

      <Box sx={{ flex: 1, overflowY: "scroll", background: "#fff" }} className="list--editor">
        {!isLoading && totalCount === 0 && !list.items?.length && (
          <Box sx={{ py: 3, textAlign: "center" }}>No data</Box>
        )}
        {languageTranslation &&
          validationTemplate &&
          validationRules &&
          list.items?.map((item, index) => (
            <TranslationItem
              data={item}
              index={index}
              isActiveItem={activeItemIndex === index}
              key={item.translation.id}
              languageTranslation={languageTranslation!}
              validationTemplate={validationTemplate}
              validationRules={validationRules as ValidationRules}
              canApprove={canApprove}
              setActiveItemIndex={setActiveItemIndex}
              setActivePanel={setActivePanel}
              goTo={goTo}
              setListItemErrors={setListItemErrors}
              setListItemApprove={setListItemApprove}
              setListItem={setListItem}
              syncApprovedCount={syncApprovedCount}
            />
          ))}

        {canLoadMore ? (
          <div style={{ padding: 10, background: "transparent" }} ref={loaderRef}>
            {isLoading && <Loading className="b-loading--tiny" />}
          </div>
        ) : (
          isLoading && <Loading className="b-loading--tiny" />
        )}
      </Box>

      <Panels ref={panelRef} onPanelResizeStart={onPanelResizeStart} />
    </div>
  );
};

export const EditorPage = () => (
  <EditorProvider>
    <EditorContent />
  </EditorProvider>
);
